import { z } from 'zod'

/**
 * Common schema factories and patterns for reuse across the validation layer
 * Using Zod 4 features for better type safety and reduced duplication
 */

// Timestamp schemas for consistent date handling
export const timestampSchemas = {
  string: z.object({
    createdAt: z.string(),
    updatedAt: z.string()
  }),
  date: z.object({
    createdAt: z.date(),
    updatedAt: z.date()
  }),
  coerceDate: z.object({
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date()
  }),
  dateTime: z.object({
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
  })
} as const

// Base entity schema with common fields
export const baseEntitySchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
})

// Schema factories for common patterns
export const createPaginatedSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    total: z.number().int().min(0),
    loading: z.boolean(),
    error: z.string().nullable()
  })

export const createFilterableSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    filterParams: z.record(z.string(), z.unknown()).nullable(),
    loading: z.boolean(),
    error: z.string().nullable()
  })

// Media dimensions schema
export const dimensionsSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
  aspectRatio: z.number().optional(),
  dpi: z.number().optional()
})

export const nullableDimensionsSchema = z.object({
  width: z.number().nullable(),
  height: z.number().nullable(),
  aspectRatio: z.number().nullable(),
  dpi: z.number().optional().nullable()
})

// Address schemas for forms and payments
export const baseAddressSchema = z.object({
  name: z.string(),
  address_line1: z.string(),
  address_line2: z.string().optional(),
  city: z.string(),
  province: z.string().length(2),
  country: z.string().length(2),
  postal_code: z.string(),
  phone_number: z.string().optional(),
  email_address: z.string().email().optional()
})

export const optionalAddressSchema = baseAddressSchema.partial()

export const createRequiredAddressSchema = (prefix: 'billing' | 'shipping') =>
  baseAddressSchema.extend({
    name: z.string().min(1, `${prefix} name is required`),
    address_line1: z.string().min(1, `${prefix} address is required`),
    city: z.string().min(1, `${prefix} city is required`),
    postal_code: z.string().min(1, 'Postal code is required')
  })

// Custom reference fields factory (e.g., for Bambora custom fields)
export const createCustomRefSchema = (count: number = 5) => {
  const refs: Record<string, z.ZodOptional<z.ZodString>> = {}
  for (let i = 1; i <= count; i++) {
    refs[`ref${i}`] = z.string().optional()
  }
  return z.object(refs)
}

// Cookie consent schemas
export const cookieConsentBaseSchema = z.object({
  essential: z.boolean(),
  functional: z.boolean(),
  analytics: z.boolean(),
  advertising: z.boolean()
})

export const cookieConsentExtendedSchema = cookieConsentBaseSchema.extend({
  social: z.boolean()
})

// Integration consent schema factory
export const createIntegrationConsentSchema = () => z.object({
  dataAccess: z.boolean(),
  dataSharing: z.boolean(),
  emailAccess: z.boolean(),
  calendarAccess: z.boolean(),
  fileAccess: z.boolean()
})

// Status enum for consistent status fields
export const statusEnum = z.enum(['active', 'inactive', 'pending', 'completed', 'failed', 'cancelled'])

// Common field schemas
export const slugSchema = z.string()
  .min(3, 'Slug must be at least 3 characters')
  .max(255, 'Slug too long')
  .regex(/^[a-z0-9-]+$/, { message: 'Slug can only contain lowercase letters, numbers, and hyphens' })

export const emailSchema = z.string().email('Invalid email address')

export const urlSchema = z.string().url('Invalid URL')

export const phoneSchema = z.string().regex(
  /^\+?[1-9]\d{1,14}$/,
  'Invalid phone number format'
)

// Utility type helpers
export type InferSchema<T extends z.ZodTypeAny> = z.infer<T>